import * as React from 'react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import { XIcon } from 'lucide-react'

import { cn } from '@/shared/lib/utils'
import { OPEN_MULTI_SELECT_SELECTOR } from '@/shared/ui/multi-select-combobox'

const Sheet = DialogPrimitive.Root
const SheetTrigger = DialogPrimitive.Trigger
const SheetClose = DialogPrimitive.Close
const SheetPortal = DialogPrimitive.Portal

const SheetOverlay = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) => (
  <DialogPrimitive.Overlay
    data-slot="sheet-overlay"
    className={cn(
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50',
      className,
    )}
    {...props}
  />
)

type SheetContentProps = React.ComponentProps<typeof DialogPrimitive.Content> & {
  side?: 'left' | 'right' | 'bottom'
  /** Visible to assistive technologies only by default. */
  title: string
  description?: string
  hideCloseButton?: boolean
}

const SheetContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(
  (
    {
      className,
      children,
      side = 'left',
      title,
      description,
      hideCloseButton = false,
      onEscapeKeyDown,
      ...props
    },
    ref,
  ) => (
    <SheetPortal>
      <SheetOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-slot="sheet-content"
        onEscapeKeyDown={(event) => {
          // Escape inside an open filter dropdown closes only the dropdown.
          if (event.target instanceof Element && event.target.closest(OPEN_MULTI_SELECT_SELECTOR)) {
            event.preventDefault()
          }
          onEscapeKeyDown?.(event)
        }}
        className={cn(
          'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex min-h-0 flex-col gap-0 shadow-lg transition ease-in-out data-[state=closed]:duration-200 data-[state=open]:duration-300',
          /* dvh: stable height with mobile URL bar; min-h-0: flex child scroll areas work */
          side === 'left' &&
            'inset-y-0 left-0 h-[100dvh] max-h-[100dvh] w-[min(100vw,20rem)] max-w-full border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
          side === 'right' &&
            'inset-y-0 right-0 h-[100dvh] max-h-[100dvh] w-[min(100vw,20rem)] max-w-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
          side === 'bottom' &&
            'inset-x-0 bottom-0 max-h-[85dvh] w-full overflow-y-auto border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
          className,
        )}
        {...props}
      >
        <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
        <DialogPrimitive.Description className="sr-only">
          {description ?? title}
        </DialogPrimitive.Description>
        {children}
        {!hideCloseButton && (
          <DialogPrimitive.Close
            className={cn(
              'ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 cursor-pointer rounded-xs p-1 opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none',
              side === 'right' && 'left-4',
              side === 'left' && 'right-4',
            )}
          >
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </SheetPortal>
  ),
)
SheetContent.displayName = 'SheetContent'

export { Sheet, SheetClose, SheetContent, SheetPortal, SheetTrigger }
